/**
 * IPs asignadas de un servidor (variante tarjetas).
 *
 * Sustituye visualmente a la tabla de `ElementIpPanel`: misma fuente de datos
 * (IPAM), pero con la tabla `sf2-table` y colores por tokens, legibles en tema
 * claro y oscuro. La gestión completa (alta, edición, primaria, borrado) sigue
 * en `ElementIpPanel`, plegada en «Gestionar IPs».
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const TIPOS = {
  static: { label: 'estático', cls: 'sf2-pill--ac' },
  dhcp: { label: 'DHCP', cls: 'sf2-pill--wa' },
  vip: { label: 'VIP', cls: 'sf2-pill--info' },
  vrrp: { label: 'VRRP', cls: 'sf2-pill--info' },
  hsrp: { label: 'HSRP', cls: 'sf2-pill--info' },
  virtual: { label: 'virtual', cls: 'sf2-pill--info' },
  carp: { label: 'CARP', cls: 'sf2-pill--vi' },
};

function etiquetaInterfaz(ref, nicMap) {
  if (!ref) return '—';
  if (ref === 'bmc') return 'BMC / iLO';
  if (ref === 'mgmt') return 'mgmt';
  if (ref.startsWith('team:')) return `team ${ref.slice(5)}`;
  if (ref.startsWith('nic-')) return nicMap[ref] || ref;
  return ref;
}

export default function ServerCardsIps({
  objectType = 'server',
  objectId,
  companyId,
  onCountChange,
}) {
  const [asignaciones, setAsignaciones] = useState([]);
  const [nicMap, setNicMap] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!objectId || !companyId) {
      setAsignaciones([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    setError('');
    try {
      const consultas = [
        api.getIpamAssignments(companyId, { object_type: objectType, object_id: objectId }),
      ];
      if (objectType === 'server') {
        consultas.push(
          api.getIpamAssignments(companyId, { object_type: 'server_team', object_id: objectId }),
          api.getIpamAssignments(companyId, { object_type: 'rack_element', object_id: objectId }),
        );
      }
      const resultados = await Promise.all(consultas);
      const vistos = new Set();
      const todas = resultados
        .flatMap((r) => r.data || [])
        .filter((a) => {
          if (a.status !== 'active') return false;
          if (vistos.has(a.id)) return false;
          vistos.add(a.id);
          return true;
        });
      setAsignaciones(todas);

      if (objectType === 'server') {
        try {
          const res = await api.getComponents(objectId, companyId);
          const map = {};
          (res?.data || []).forEach((c) => {
            if (c.component_type === 'nic' || c.component_type === 'hba') {
              (c.ports || []).forEach((p) => {
                map[`nic-${p.id}`] = `${c.model || 'NIC'} · ${p.label || `P${p.port_number}`}`;
              });
            }
          });
          setNicMap(map);
        } catch {
          /* sin componentes: se muestra la referencia tal cual */
        }
      }
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las IPs');
    } finally {
      setCargando(false);
    }
  }, [objectType, objectId, companyId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { onCountChange?.(asignaciones.length); }, [asignaciones.length, onCountChange]);

  const ordenadas = useMemo(() => (
    [...asignaciones].sort((a, b) => (
      Number(b.is_primary) - Number(a.is_primary)
      || String(a.ip || '').localeCompare(String(b.ip || ''), undefined, { numeric: true })
    ))
  ), [asignaciones]);

  if (cargando) return <div className="sf2-empty">Cargando IPs…</div>;
  if (error) return <div className="sf2-empty">{error}</div>;
  if (!asignaciones.length) {
    return (
      <div className="sf2-empty">
        Sin IPs asignadas. Usa «Gestionar IPs» para añadir una.
      </div>
    );
  }

  return (
    <div className="sf2-table-wrap">
      <table className="sf2-table sf2-ip-table">
        <thead>
          <tr>
            <th aria-label="Primaria" />
            <th>IP</th>
            <th>Subred</th>
            <th>Interfaz</th>
            <th>Tipo</th>
            <th>DNS</th>
          </tr>
        </thead>
        <tbody>
          {ordenadas.map((a) => {
            const tipo = TIPOS[a.ip_type] || { label: a.ip_type || '—', cls: '' };
            return (
              <tr key={a.id} className={a.is_primary ? 'sf2-ip-primary' : ''}>
                <td className="sf2-ip-star" title={a.is_primary ? 'IP primaria' : 'IP secundaria'}>
                  {a.is_primary ? '★' : '☆'}
                </td>
                <td><b className="sf2-ip-addr">{a.ip || '—'}</b></td>
                <td>{a.subnet_cidr || '—'}</td>
                <td>{etiquetaInterfaz(a.interface_ref, nicMap)}</td>
                <td><span className={`sf2-pill ${tipo.cls}`}>{tipo.label}</span></td>
                <td>{a.dns_name || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
